import React, { useState, useEffect } from 'react';
import {
  IconPlus, IconTrash, IconEdit, IconEye, IconEyeOff,
  IconCheck, IconX, IconCopy, IconAlertCircle, IconLock
} from '@tabler/icons-react';
import api from '../api/api';

export default function ApiKeyManagePage({ showToast: externalShowToast }) {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [toast, setToast] = useState(null);

  // 내부 showToast와 외부 showToast 통합
  const showToast = externalShowToast || ((message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  });

  // 생성 모달
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    scopes: 'ALL',
    expiresAt: ''
  });

  // 편집 모달
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    scopes: 'ALL',
    expiresAt: ''
  });

  // 마스킹 상태
  const [showKeys, setShowKeys] = useState({});

  // 삭제 확인
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    fetchApiKeys();
  }, [page]);

  const fetchApiKeys = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api-keys', {
        params: { page, size: 10 }
      });
      setApiKeys(response.data.content || []);
      setTotalPages(response.data.totalPages || 0);
    } catch (error) {
      showToast('API 키 목록을 불러오는 중 오류가 발생했습니다.', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    setCreateForm({
      name: '',
      description: '',
      scopes: 'ALL',
      expiresAt: ''
    });
    setNewKey(null);
    setShowCreateModal(true);
  };

  const handleCreateSubmit = async () => {
    if (!createForm.name.trim()) {
      showToast('API 키 이름을 입력하세요.', 'error');
      return;
    }

    try {
      const payload = {
        ...createForm,
        expiresAt: createForm.expiresAt ? createForm.expiresAt + 'T23:59:59' : null
      };
      const response = await api.post('/api-keys', payload);
      setNewKey(response.data);
      setCreateForm({
        name: '',
        description: '',
        scopes: 'ALL',
        expiresAt: ''
      });
      await fetchApiKeys();
      showToast('API 키가 생성되었습니다.', 'success');
    } catch (error) {
      showToast('API 키 생성 중 오류가 발생했습니다.', 'error');
      console.error(error);
    }
  };

  const handleEditClick = (apiKey) => {
    setEditingKey(apiKey);
    setEditForm({
      name: apiKey.name,
      description: apiKey.description,
      scopes: apiKey.scopes,
      expiresAt: apiKey.expiresAt ? apiKey.expiresAt.split('T')[0] : ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    if (!editForm.name.trim()) {
      showToast('API 키 이름을 입력하세요.', 'error');
      return;
    }

    try {
      const payload = {
        ...editForm,
        expiresAt: editForm.expiresAt ? editForm.expiresAt + 'T23:59:59' : null
      };
      await api.put(`/api-keys/${editingKey.id}`, payload);
      setShowEditModal(false);
      await fetchApiKeys();
      showToast('API 키가 수정되었습니다.', 'success');
    } catch (error) {
      showToast('API 키 수정 중 오류가 발생했습니다.', 'error');
      console.error(error);
    }
  };

  const handleRevokeClick = async (id) => {
    if (!window.confirm('이 API 키를 폐기하시겠습니까?')) return;

    try {
      await api.post(`/api-keys/${id}/revoke`);
      await fetchApiKeys();
      showToast('API 키가 폐기되었습니다.', 'success');
    } catch (error) {
      showToast('API 키 폐기 중 오류가 발생했습니다.', 'error');
      console.error(error);
    }
  };

  const handleDeleteClick = (apiKey) => {
    setDeleteTarget(apiKey);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/api-keys/${deleteTarget.id}`);
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      await fetchApiKeys();
      showToast('API 키가 삭제되었습니다.', 'success');
    } catch (error) {
      showToast('API 키 삭제 중 오류가 발생했습니다.', 'error');
      console.error(error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToast('복사되었습니다.', 'success');
  };

  const getStatusBadge = (status) => {
    const badges = {
      'ACTIVE': { bg: 'bg-success', text: '활성' },
      'REVOKED': { bg: 'bg-danger', text: '폐기됨' },
      'EXPIRED': { bg: 'bg-warning', text: '만료됨' }
    };
    const badge = badges[status] || badges['ACTIVE'];
    return <span className={`badge ${badge.bg}`}>{badge.text}</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ko-KR');
  };

  return (
    <div className="container-xl mt-4">
      {/* 토스트 메시지 */}
      {toast && (
        <div className={`alert alert-${toast.type === 'success' ? 'success' : toast.type === 'error' ? 'danger' : 'info'} alert-dismissible fade show`} role="alert">
          {toast.message}
          <button type="button" className="btn-close" onClick={() => setToast(null)}></button>
        </div>
      )}

      <div className="page-wrapper">
        <div className="container-xl">
          {/* 헤더 */}
          <div className="page-header d-print-none">
            <div className="row align-items-center">
              <div className="col">
                <h2 className="page-title">API 키 관리</h2>
                <div className="text-muted mt-1">타사 API 키 및 토큰을 안전하게 관리합니다.</div>
              </div>
              <div className="col-auto">
                <button
                  className="btn btn-sm btn-primary d-none d-sm-inline-block"
                  onClick={handleCreateClick}>
                  <IconPlus size={18} className="me-2" />
                  API 키 생성
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="page-body">
          <div className="container-xl">
            {/* API 키 목록 */}
            <div className="row row-deck row-cards">
              <div className="col-12">
                <div className="card">
                  <div className="table-responsive">
                    {loading ? (
                      <div className="p-4 text-center">로드 중...</div>
                    ) : apiKeys.length === 0 ? (
                      <div className="p-4 text-center text-muted">
                        <IconLock size={48} className="mb-3 opacity-50" />
                        <p>생성된 API 키가 없습니다.</p>
                        <button className="btn btn-sm btn-primary" onClick={handleCreateClick}>
                          API 키 생성하기
                        </button>
                      </div>
                    ) : (
                      <table className="table card-table table-vcenter text-nowrap">
                        <thead>
                          <tr>
                            <th>이름</th>
                            <th>키</th>
                            <th>권한</th>
                            <th>상태</th>
                            <th>만료일</th>
                            <th>마지막 사용</th>
                            <th>생성일</th>
                            <th style={{ width: '120px' }}>작업</th>
                          </tr>
                        </thead>
                        <tbody>
                          {apiKeys.map((apiKey) => (
                            <tr key={apiKey.id}>
                              <td>
                                <div className="font-weight-bold">{apiKey.name}</div>
                                {apiKey.description && <div className="text-muted text-sm">{apiKey.description}</div>}
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <code>
                                    {showKeys[apiKey.id] ? apiKey.maskedKey : '••••••••••'}
                                  </code>
                                  <button
                                    className="btn btn-sm btn-ghost-primary p-1"
                                    onClick={() => setShowKeys({
                                      ...showKeys,
                                      [apiKey.id]: !showKeys[apiKey.id]
                                    })}
                                    title={showKeys[apiKey.id] ? '숨기기' : '보기'}>
                                    {showKeys[apiKey.id] ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                                  </button>
                                </div>
                              </td>
                              <td>{apiKey.scopes}</td>
                              <td>{getStatusBadge(apiKey.status)}</td>
                              <td>{formatDate(apiKey.expiresAt)}</td>
                              <td>{formatDate(apiKey.lastUsedAt)}</td>
                              <td>{formatDate(apiKey.createdAt)}</td>
                              <td>
                                <div className="d-flex align-items-center gap-1 flex-nowrap">
                                  <button
                                    className="btn btn-icon btn-sm btn-ghost-primary"
                                    onClick={() => handleEditClick(apiKey)}
                                    title="수정">
                                    <IconEdit size={16} />
                                  </button>
                                  {apiKey.status === 'ACTIVE' && (
                                    <button
                                      className="btn btn-icon btn-sm btn-ghost-warning"
                                      onClick={() => handleRevokeClick(apiKey.id)}
                                      title="폐기">
                                      <IconX size={16} />
                                    </button>
                                  )}
                                  <button
                                    className="btn btn-icon btn-sm btn-ghost-danger"
                                    onClick={() => handleDeleteClick(apiKey)}
                                    title="삭제">
                                    <IconTrash size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* 페이지네이션 */}
                  {totalPages > 1 && (
                    <div className="card-footer d-flex align-items-center">
                      <p className="text-muted">
                        페이지 {page + 1} / {totalPages}
                      </p>
                      <div className="ms-auto">
                        <button
                          className="btn btn-sm"
                          disabled={page === 0}
                          onClick={() => setPage(page - 1)}>
                          이전
                        </button>
                        <button
                          className="btn btn-sm"
                          disabled={page >= totalPages - 1}
                          onClick={() => setPage(page + 1)}>
                          다음
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 생성 모달 */}
      {showCreateModal && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">API 키 생성</h5>
                <button type="button" className="btn-close" onClick={() => setShowCreateModal(false)}></button>
              </div>
              <div className="modal-body">
                {newKey ? (
                  <div className="alert alert-info">
                    <div className="d-flex">
                      <div>
                        <h5>✓ API 키가 생성되었습니다</h5>
                        <p className="mb-3">다음은 이 키의 유일한 사본입니다. 안전한 곳에 저장하세요.</p>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control form-control-sm font-monospace"
                            value={newKey.keyValue}
                            readOnly
                          />
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => copyToClipboard(newKey.keyValue)}>
                            <IconCopy size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="mb-3">
                      <label className="form-label">API 키 이름 *</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={createForm.name}
                        onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                        placeholder="예: Payment Gateway"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">설명</label>
                      <textarea
                        className="form-control form-control-sm"
                        rows="2"
                        value={createForm.description}
                        onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                        placeholder="이 API 키의 용도를 설명하세요."
                      ></textarea>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">권한</label>
                      <select
                        className="form-select form-select-sm"
                        value={createForm.scopes}
                        onChange={(e) => setCreateForm({ ...createForm, scopes: e.target.value })}>
                        <option value="ALL">모든 권한</option>
                        <option value="PAYMENT">결제만</option>
                        <option value="MESSAGE">메시지만</option>
                        <option value="STORAGE">저장소만</option>
                        <option value="PAYMENT,MESSAGE">결제 + 메시지</option>
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">만료일 (선택)</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={createForm.expiresAt}
                        onChange={(e) => setCreateForm({ ...createForm, expiresAt: e.target.value })}
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => setShowCreateModal(false)}>
                  {newKey ? '닫기' : '취소'}
                </button>
                {!newKey && (
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={handleCreateSubmit}>
                    생성
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 편집 모달 */}
      {showEditModal && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">API 키 수정</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">API 키 이름 *</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">설명</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows="2"
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label className="form-label">권한</label>
                  <select
                    className="form-select form-select-sm"
                    value={editForm.scopes}
                    onChange={(e) => setEditForm({ ...editForm, scopes: e.target.value })}>
                    <option value="ALL">모든 권한</option>
                    <option value="PAYMENT">결제만</option>
                    <option value="MESSAGE">메시지만</option>
                    <option value="STORAGE">저장소만</option>
                    <option value="PAYMENT,MESSAGE">결제 + 메시지</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">만료일 (선택)</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={editForm.expiresAt}
                    onChange={(e) => setEditForm({ ...editForm, expiresAt: e.target.value })}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => setShowEditModal(false)}>
                  취소
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleEditSubmit}>
                  수정
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteConfirm && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">API 키 삭제</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteConfirm(false)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-danger d-flex">
                  <IconAlertCircle size={20} className="me-3 flex-shrink-0" />
                  <div>
                    <h5>이 작업은 되돌릴 수 없습니다.</h5>
                    <p className="mb-0">
                      "<strong>{deleteTarget?.name}</strong>" API 키를 삭제하시겠습니까?
                    </p>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => setShowDeleteConfirm(false)}>
                  취소
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={confirmDelete}>
                  삭제
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 오버레이 */}
      {(showCreateModal || showEditModal || showDeleteConfirm) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
}
